import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import type { Edge as RFEdge, Node as RFNode } from 'reactflow';
import {
  NODE_STATUS_LABELS,
  NODE_TYPE_COLORS,
  NodeStatus,
  type NodeData,
  type Priority,
} from '@/types/node.types';
import {
  LINK_TYPE_COLORS,
  LinkType,
  LinkIntensity,
  type LinkData,
} from '@/types/link.types';
import { useCanvasStore } from '@/stores/canvas.store';
import CommentsPanel from './CommentsPanel';

interface NodeInspectorProps {
  nodeId: string | null;
  workspaceId: string;
  onClose: () => void;
  onFocusNode: (id: string) => void;
}

const NODE_ICONS: Record<string, string> = {
  Vision: '★',
  Objective: '◎',
  Activity: '⚡',
  Risk: '⚠',
  Opportunity: '✨',
  Resource: '⚙',
  KPI: '📊',
  Stakeholder: '\u{1F465}',
};

const TYPE_LABELS: Record<string, string> = {
  Vision: 'Vision',
  Objective: 'Objectif',
  Activity: 'Activité',
  Risk: 'Risque',
  Opportunity: 'Opportunité',
  Resource: 'Ressource',
  KPI: 'KPI',
  Stakeholder: 'Partie prenante',
};

const toNumberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Semantic labels for link types, per direction.
 * "out" = the current node is the SOURCE, "in" = the current node is the TARGET.
 */
const LINK_LABELS: Record<LinkType, { out: string; in: string }> = {
  [LinkType.Hierarchy]: { out: 'Enfants', in: 'Parents' },
  [LinkType.Dependency]: { out: 'Dépend de', in: 'Dont dépend' },
  [LinkType.Contribution]: { out: 'Contribue à', in: 'Contributions reçues' },
  [LinkType.Contradiction]: { out: 'Freine', in: 'Freiné par' },
  [LinkType.Complementarity]: { out: 'Complémente', in: 'Complété par' },
  [LinkType.FreeForm]: { out: 'Lien libre (sortant)', in: 'Lien libre (entrant)' },
};

type Tab = 'details' | 'tracking' | 'relations' | 'comments';

export const NodeInspector: FC<NodeInspectorProps> = ({
  nodeId,
  workspaceId,
  onClose,
  onFocusNode,
}) => {
  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);
  const updateNode = useCanvasStore((s) => s.updateNode);
  const removeNode = useCanvasStore((s) => s.removeNode);

  const [tab, setTab] = useState<Tab>('details');

  const node = useMemo<RFNode<NodeData> | null>(
    () => nodes.find((n) => n.id === nodeId && n.type !== 'group') ?? null,
    [nodes, nodeId],
  );

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>(3);
  const [status, setStatus] = useState<NodeStatus>(NodeStatus.Active);
  const [budgetPlanned, setBudgetPlanned] = useState<string>('');
  const [budgetSpent, setBudgetSpent] = useState<string>('');
  const [effortPlannedDays, setEffortPlannedDays] = useState<string>('');
  const [effortSpentDays, setEffortSpentDays] = useState<string>('');
  const [progress, setProgress] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!node) return;
    setTitle(node.data.title);
    setDescription(node.data.description ?? '');
    setPriority(node.data.priority);
    setStatus(node.data.status);
    const bp = toNumberOrNull(node.data.budgetPlanned);
    const bs = toNumberOrNull(node.data.budgetSpent);
    const ep = toNumberOrNull(node.data.effortPlannedDays);
    const es = toNumberOrNull(node.data.effortSpentDays);
    const pg = toNumberOrNull(node.data.progress);
    setBudgetPlanned(bp === null ? '' : String(bp));
    setBudgetSpent(bs === null ? '' : String(bs));
    setEffortPlannedDays(ep === null ? '' : String(ep));
    setEffortSpentDays(es === null ? '' : String(es));
    setProgress(pg === null ? '' : String(pg));
    setTab('details');
  }, [node?.id]);

  // Group incident edges by type + direction
  const groupedRelations = useMemo(() => {
    if (!nodeId) return [] as Array<{ type: LinkType; dir: 'in' | 'out'; edges: RFEdge<LinkData>[] }>;
    const res: Array<{ type: LinkType; dir: 'in' | 'out'; edges: RFEdge<LinkData>[] }> = [];
    for (const type of Object.values(LinkType)) {
      const out = edges.filter(
        (e) =>
          e.source === nodeId &&
          (e.data as { linkType?: LinkType } | undefined)?.linkType === type,
      );
      const incoming = edges.filter(
        (e) =>
          e.target === nodeId &&
          (e.data as { linkType?: LinkType } | undefined)?.linkType === type,
      );
      if (out.length) res.push({ type, dir: 'out', edges: out });
      if (incoming.length) res.push({ type, dir: 'in', edges: incoming });
    }
    return res;
  }, [edges, nodeId]);

  if (!node) return null;
  const color = NODE_TYPE_COLORS[node.data.type] || '#6B7280';

  const parseNumField = (v: string): number | null => {
    const trimmed = v.trim().replace(',', '.');
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNode(node.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        status,
        budgetPlanned: parseNumField(budgetPlanned),
        budgetSpent: parseNumField(budgetSpent),
        effortPlannedDays: parseNumField(effortPlannedDays),
        effortSpentDays: parseNumField(effortSpentDays),
        progress: parseNumField(progress),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer le noeud "${node.data.title}" ?`)) return;
    await removeNode(node.id);
    onClose();
  };

  return (
    <div
      className="node-inspector"
      style={{
        position: 'fixed',
        top: 'var(--header-height)',
        right: 0,
        width: 380,
        maxWidth: '100vw',
        height: 'calc(100vh - var(--header-height))',
        backgroundColor: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 40,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 'var(--spacing-md)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div
          style={{
            width: 4,
            alignSelf: 'stretch',
            backgroundColor: color,
            borderRadius: 2,
          }}
        />
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {NODE_ICONS[node.data.type] ?? '●'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {TYPE_LABELS[node.data.type]} · P{node.data.priority} · {NODE_STATUS_LABELS[node.data.status]}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--color-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {node.data.title}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Fermer"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            fontSize: 20,
            padding: 4,
          }}
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)' }}>
        <TabBtn active={tab === 'details'} onClick={() => setTab('details')}>
          Détails
        </TabBtn>
        <TabBtn active={tab === 'tracking'} onClick={() => setTab('tracking')}>
          Suivi
        </TabBtn>
        <TabBtn active={tab === 'relations'} onClick={() => setTab('relations')}>
          Relations
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              padding: '1px 6px',
              borderRadius: 999,
              backgroundColor: 'var(--color-border)',
              color: 'var(--color-text-muted)',
            }}
          >
            {groupedRelations.reduce((acc, g) => acc + g.edges.length, 0)}
          </span>
        </TabBtn>
        <TabBtn active={tab === 'comments'} onClick={() => setTab('comments')}>
          Commentaires
        </TabBtn>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-md)' }}>
        {tab === 'details' && (
          <DetailsTab
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            priority={priority}
            setPriority={setPriority}
            status={status}
            setStatus={setStatus}
            color={color}
          />
        )}
        {tab === 'tracking' && (
          <TrackingTab
            budgetPlanned={budgetPlanned}
            setBudgetPlanned={setBudgetPlanned}
            budgetSpent={budgetSpent}
            setBudgetSpent={setBudgetSpent}
            effortPlannedDays={effortPlannedDays}
            setEffortPlannedDays={setEffortPlannedDays}
            effortSpentDays={effortSpentDays}
            setEffortSpentDays={setEffortSpentDays}
            progress={progress}
            setProgress={setProgress}
            color={color}
          />
        )}
        {tab === 'relations' && (
          <RelationsTab
            grouped={groupedRelations}
            nodes={nodes}
            currentId={node.id}
            onFocus={onFocusNode}
          />
        )}
        {tab === 'comments' && (
          <div style={{ margin: -16 }}>
            <CommentsPanelInline nodeId={node.id} workspaceId={workspaceId} />
          </div>
        )}
      </div>

      {/* Footer (save/delete on Détails + Suivi) */}
      {(tab === 'details' || tab === 'tracking') && (
        <div style={{ display: 'flex', gap: 8, padding: 'var(--spacing-md)', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={handleDelete}
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid #EF4444',
              backgroundColor: 'transparent',
              color: '#EF4444',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Supprimer
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              backgroundColor: color,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: saving || !title.trim() ? 'not-allowed' : 'pointer',
              opacity: saving || !title.trim() ? 0.5 : 1,
            }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---- sub-components --------------------------------------------------------

const TabBtn: FC<{ active: boolean; onClick: () => void; children: ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      padding: '10px 8px',
      background: 'transparent',
      border: 'none',
      borderBottom: active
        ? '2px solid var(--color-primary)'
        : '2px solid transparent',
      color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
      fontSize: 13,
      fontWeight: active ? 600 : 400,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {children}
  </button>
);

const DetailsTab: FC<{
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  priority: Priority;
  setPriority: (p: Priority) => void;
  status: NodeStatus;
  setStatus: (s: NodeStatus) => void;
  color: string;
}> = ({ title, setTitle, description, setDescription, priority, setPriority, status, setStatus, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div>
      <Label>Titre</Label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        style={{
          width: '100%',
          marginTop: 6,
          padding: '9px 12px',
          fontSize: 14,
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          outline: 'none',
        }}
      />
    </div>
    <div>
      <Label>Description</Label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        style={{
          width: '100%',
          marginTop: 6,
          padding: '9px 12px',
          fontSize: 13,
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          outline: 'none',
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
      />
    </div>
    <div>
      <Label>Priorité</Label>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {[1, 2, 3, 4, 5].map((p) => (
          <button
            key={p}
            onClick={() => setPriority(p as Priority)}
            style={{
              flex: 1,
              padding: '8px 0',
              borderRadius: 8,
              border: `1.5px solid ${priority === p ? color : 'var(--color-border)'}`,
              backgroundColor: priority === p ? `${color}22` : 'transparent',
              color: priority === p ? color : 'var(--color-text-muted)',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            P{p}
          </button>
        ))}
      </div>
    </div>
    <div>
      <Label>Statut</Label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {Object.values(NodeStatus).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: `1.5px solid ${status === s ? 'var(--color-primary)' : 'var(--color-border)'}`,
              backgroundColor: status === s ? 'var(--color-primary)22' : 'transparent',
              color: status === s ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {NODE_STATUS_LABELS[s]}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const TrackingTab: FC<{
  budgetPlanned: string;
  setBudgetPlanned: (v: string) => void;
  budgetSpent: string;
  setBudgetSpent: (v: string) => void;
  effortPlannedDays: string;
  setEffortPlannedDays: (v: string) => void;
  effortSpentDays: string;
  setEffortSpentDays: (v: string) => void;
  progress: string;
  setProgress: (v: string) => void;
  color: string;
}> = ({
  budgetPlanned,
  setBudgetPlanned,
  budgetSpent,
  setBudgetSpent,
  effortPlannedDays,
  setEffortPlannedDays,
  effortSpentDays,
  setEffortSpentDays,
  progress,
  setProgress,
  color,
}) => {
  const parseNum = (v: string): number | null => {
    const t = v.trim().replace(',', '.');
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const plannedN = parseNum(budgetPlanned);
  const spentN = parseNum(budgetSpent);
  const overBudget = plannedN !== null && spentN !== null && spentN > plannedN;

  const effortPlannedN = parseNum(effortPlannedDays);
  const effortSpentN = parseNum(effortSpentDays);
  const overEffort =
    effortPlannedN !== null && effortSpentN !== null && effortSpentN > effortPlannedN;

  const progressN = parseNum(progress);
  const clampedProgress =
    progressN !== null ? Math.max(0, Math.min(100, progressN)) : null;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    marginTop: 6,
    padding: '9px 12px',
    fontSize: 14,
    backgroundColor: 'var(--color-background)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    outline: 'none',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          fontSize: 12,
          color: 'var(--color-text-muted)',
          lineHeight: 1.5,
        }}
      >
        Champs optionnels. Laisse vide pour ne pas afficher.
      </div>

      <div>
        <Label>Avancement (%)</Label>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={progress}
          onChange={(e) => setProgress(e.target.value)}
          placeholder="0–100"
          style={inputStyle}
        />
        {clampedProgress !== null && (
          <div
            style={{
              marginTop: 8,
              height: 6,
              borderRadius: 3,
              backgroundColor: 'var(--color-border)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${clampedProgress}%`,
                height: '100%',
                backgroundColor: color,
                transition: 'width 200ms ease',
              }}
            />
          </div>
        )}
      </div>

      <div>
        <Label>Budget (€)</Label>
        <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Projeté</div>
            <input
              type="number"
              min={0}
              step="0.01"
              value={budgetPlanned}
              onChange={(e) => setBudgetPlanned(e.target.value)}
              placeholder="0"
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Consommé</div>
            <input
              type="number"
              min={0}
              step="0.01"
              value={budgetSpent}
              onChange={(e) => setBudgetSpent(e.target.value)}
              placeholder="0"
              style={{
                ...inputStyle,
                borderColor: overBudget ? '#EF4444' : 'var(--color-border)',
                color: overBudget ? '#EF4444' : 'var(--color-text)',
              }}
            />
          </div>
        </div>
        {overBudget && plannedN !== null && spentN !== null && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#EF4444', fontWeight: 600 }}>
            ⚠ Dépassement budget : {(spentN - plannedN).toLocaleString('fr-FR')} €
          </div>
        )}
      </div>

      <div>
        <Label>Effort (jours-homme)</Label>
        <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Projeté</div>
            <input
              type="number"
              min={0}
              step="0.5"
              value={effortPlannedDays}
              onChange={(e) => setEffortPlannedDays(e.target.value)}
              placeholder="0"
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Réalisé</div>
            <input
              type="number"
              min={0}
              step="0.5"
              value={effortSpentDays}
              onChange={(e) => setEffortSpentDays(e.target.value)}
              placeholder="0"
              style={{
                ...inputStyle,
                borderColor: overEffort ? '#EF4444' : 'var(--color-border)',
                color: overEffort ? '#EF4444' : 'var(--color-text)',
              }}
            />
          </div>
        </div>
        {overEffort && effortPlannedN !== null && effortSpentN !== null && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#EF4444', fontWeight: 600 }}>
            ⚠ Dépassement effort : {(effortSpentN - effortPlannedN).toLocaleString('fr-FR')} j
          </div>
        )}
      </div>
    </div>
  );
};

const RelationsTab: FC<{
  grouped: Array<{ type: LinkType; dir: 'in' | 'out'; edges: RFEdge<LinkData>[] }>;
  nodes: RFNode<NodeData>[];
  currentId: string;
  onFocus: (id: string) => void;
}> = ({ grouped, nodes, currentId, onFocus }) => {
  if (grouped.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '32px 8px',
          color: 'var(--color-text-muted)',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 8 }}>🔗</div>
        Aucune liaison sur ce noeud.
        <div style={{ fontSize: 11, marginTop: 6 }}>
          Tire depuis une ancre du noeud sur le canvas pour en créer une.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {grouped.map(({ type, dir, edges }) => (
        <div key={`${type}-${dir}`}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 6,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'var(--color-text-muted)',
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: LINK_TYPE_COLORS[type],
              }}
            />
            {LINK_LABELS[type][dir]} ({edges.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {edges.map((e) => {
              const otherId = dir === 'out' ? e.target : e.source;
              const other = nodes.find((n) => n.id === otherId);
              if (!other || other.type === 'group') return null;
              const otherColor = NODE_TYPE_COLORS[other.data.type] || '#6B7280';
              const intensity = (e.data as LinkData | undefined)?.intensity;
              const label = (e.data as LinkData | undefined)?.label;
              return (
                <button
                  key={e.id}
                  onClick={() => onFocus(otherId)}
                  disabled={otherId === currentId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    textAlign: 'left',
                    backgroundColor: 'var(--color-background)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    transition: 'var(--transition-fast)',
                  }}
                  onMouseEnter={(ev) => {
                    ev.currentTarget.style.borderColor = otherColor;
                  }}
                  onMouseLeave={(ev) => {
                    ev.currentTarget.style.borderColor = 'var(--color-border)';
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      backgroundColor: otherColor,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {NODE_ICONS[other.data.type] ?? '●'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {other.data.title}
                    </div>
                    {label && (
                      <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                        « {label} »
                      </div>
                    )}
                  </div>
                  {intensity && (
                    <span
                      style={{
                        fontSize: 10,
                        padding: '2px 6px',
                        borderRadius: 999,
                        backgroundColor:
                          intensity === LinkIntensity.Strong
                            ? `${LINK_TYPE_COLORS[type]}33`
                            : intensity === LinkIntensity.Medium
                              ? `${LINK_TYPE_COLORS[type]}22`
                              : `${LINK_TYPE_COLORS[type]}11`,
                        color: LINK_TYPE_COLORS[type],
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {intensity === LinkIntensity.Strong
                        ? 'Fort'
                        : intensity === LinkIntensity.Medium
                          ? 'Moyen'
                          : 'Faible'}
                    </span>
                  )}
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>›</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const Label: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: 'var(--color-text-muted)',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}
  >
    {children}
  </div>
);

// Inline Comments wrapper — hides the slide-in chrome so CommentsPanel's
// existing markup integrates in our Inspector layout.
const CommentsPanelInline: FC<{ nodeId: string; workspaceId: string }> = ({
  nodeId,
  workspaceId,
}) => {
  // CommentsPanel already fetches / displays / posts comments. We just render
  // it as-is (it has its own slide-in container but we override positioning
  // via CSS class so it fits the tab area).
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <style>{`
        .node-inspector .comments-panel {
          position: static !important;
          width: 100% !important;
          max-width: none !important;
          height: auto !important;
          box-shadow: none !important;
          border-left: none !important;
          transform: none !important;
          transition: none !important;
        }
      `}</style>
      <CommentsPanel
        nodeId={nodeId}
        workspaceId={workspaceId}
        onClose={() => {
          /* close button inside tab is a no-op; the parent tab handles close */
        }}
      />
    </div>
  );
};

export default NodeInspector;
